import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Briefcase, Truck } from "lucide-react";
import { toast } from "sonner";

export default function RoleSelect() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(user?.role || "subcontractor");
  const [company, setCompany] = useState(user?.company || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!company.trim()) { toast.error("Company name required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/role", { role, company });
      setUser(data);
      toast.success("Profile saved");
      navigate(role === "vendor" ? "/vendor" : "/dashboard");
    } catch {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="role-select">
      <div className="tray p-10 max-w-2xl w-full">
        <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-3">Initialize Profile</div>
        <h1 className="font-display text-4xl mb-2">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-text-secondary mb-8">Choose your operating role. You can switch later.</p>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { id: "subcontractor", icon: Briefcase, title: "Subcontractor Admin", desc: "Manage Sub-to-Vendor pipeline" },
            { id: "vendor", icon: Truck, title: "Vendor Portal", desc: "Upload quotes & certifications" },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setRole(opt.id)}
              data-testid={`role-${opt.id}-btn`}
              className={`tray-inset p-5 text-left transition ${role === opt.id ? "ring-2 ring-amber-500" : ""}`}
            >
              <opt.icon className={`w-6 h-6 mb-3 ${role === opt.id ? "text-amber-500" : "text-text-secondary"}`} />
              <div className="font-display text-lg">{opt.title}</div>
              <div className="text-xs text-text-secondary mt-1">{opt.desc}</div>
            </button>
          ))}
        </div>

        <label className="block font-mono-data text-[10px] tracking-[0.2em] uppercase text-text-secondary mb-2">
          Company / Firm
        </label>
        <input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Halliburton Subcontracting LLC"
          data-testid="company-input"
          className="w-full tray-inset px-4 py-3 mb-6 bg-transparent text-white font-mono-data text-sm outline-none focus:ring-1 focus:ring-amber-500"
        />
        <button
          onClick={submit}
          disabled={saving}
          data-testid="role-save-btn"
          className="btn-tactile btn-amber w-full"
        >
          {saving ? "Saving..." : "Enter Platform"}
        </button>
      </div>
    </div>
  );
}
